import './ModifyPermissions.scss';
import {
    ISharingInformation, Mode, ISharingLinkSettings, SharingAudience, SharingLinkKind, ClientId, SharingRole,
    IEngagementExtraData, ICustomLabeledAudienceConfig, SharingAudienceTemplates, ISharingLinkTemplate
} from '../../interfaces/SharingInterfaces';
import { ActivityIndicator } from '../ActivityIndicator/ActivityIndicator';
import { ConfirmationDialog, ConfirmationCode } from '../PermissionsList/ShareLink/ConfirmationDialog/ConfirmationDialog';
import { Header } from '../Header/Header';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import { PermissionsSettings } from './PermissionsSettings/PermissionsSettings';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { ShareViewState } from '../Share/Share';
import { getLinkKind, ExpirationErrorCode, getDefaultExpirationDateValue } from '../../utilities/ShareHelper';
import { getShortDisplayDate } from '@ms/odsp-utilities/lib/dateTime/DateTime';
import stringsResx from '../Share.resx';
import * as PeoplePickerHelper from '../../utilities/PeoplePickerHelper';
import * as ClientIdHelper from '../../utilities/ClientIdHelper';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import * as SharingAbilitiesHelper from '../../utilities/SharingAbilitiesHelper';
import * as ShareHelper from '../../utilities/ShareHelper';

export interface IModifyPermissionsProps {
    clientId: ClientId;
    companyName: string;
    currentSettings: ISharingLinkSettings;
    doesCreate: boolean; // "Copy link" flow creates link on apply, "Share flow" does not.
    onCancel: () => void;
    onReady?: () => void;
    onSelectedPermissionsChange: (currentSettings: ISharingLinkSettings) => void;
    sharingInformation: ISharingInformation;
    showExistingAccessOption: boolean;
    groupsMemberCount: number;
    onViewPolicyTipClicked: () => void;
    createPeopleSharingLinks?: boolean;
    copyLinkShortcut: boolean;
    mode: Mode;
    engagementExtraData: IEngagementExtraData;
    recipients: IPerson[];
    isLoadingDefaultRecipients: boolean;
    customLabeledAudienceConfig: ICustomLabeledAudienceConfig;
    preferCustomLabeledAudience: boolean;
    onSelectedPeopleChange: (items: Array<any>) => void;
    shareId: string;
}

export interface IModifyPermissionsState {
    expirationErrorCode: ExpirationErrorCode;
    peoplePickerError: JSX.Element;
    selectedPermissions: ISharingLinkSettings;
    showActivityIndicator: boolean;
    preventDownloadDisabled: boolean;
    showConfirmationDialog: boolean;
}

export class ModifyPermissions extends React.Component<IModifyPermissionsProps, IModifyPermissionsState> {
    private _currentPassword: string = '';
    private _shouldUsePassword: boolean = false;
    private _deletedPassword: boolean;
    private _templateShareId: string;
    private _matchingTemplate: ISharingLinkTemplate = null;
    private _preventDownloadToggle: boolean;

    static contextTypes = {
        sharingStore: PropTypes.object.isRequired,
    };

    constructor(props: IModifyPermissionsProps, context: any) {
        super(props);
        this._currentPassword = '';
        this._shouldUsePassword = false;

        this.state = this._initializeState();
        this._preventDownloadToggle = !!this.state.selectedPermissions.preventDownload;

        this._initializeState = this._initializeState.bind(this);
        this._onApplyClicked = this._onApplyClicked.bind(this);
        this._onCancel = this._onCancel.bind(this);
        this._onPeoplePickerChange = this._onPeoplePickerChange.bind(this);
        this._updateExpirationErrorCode = this._updateExpirationErrorCode.bind(this);
        this.onAudienceChange = this.onAudienceChange.bind(this);
        this.onEditChange = this.onEditChange.bind(this);
        this.onRoleChange = this.onRoleChange.bind(this);
        this.onExpirationChange = this.onExpirationChange.bind(this);
        this._getShareLinkTemplate = this._getShareLinkTemplate.bind(this);
        this._findMatchingShareLinkTemplate = this._findMatchingShareLinkTemplate.bind(this);
        this._calculateRole = this._calculateRole.bind(this);
        this._onPasswordChange = this._onPasswordChange.bind(this);
        this._resetPasswordVariables = this._resetPasswordVariables.bind(this);
        this._onToggleChange = this._onToggleChange.bind(this);
        this._onConfirmationUseExisting = this._onConfirmationUseExisting.bind(this);
        this._onConfirmationUseNew = this._onConfirmationUseNew.bind(this);
    }

    public componentDidMount() {
        if (this.props.onReady) {
            this.props.onReady();
        }
    }

    public render(): React.ReactElement<{}> {
        const blockerClass: string = this.state.showActivityIndicator ? ' blocker' : '';
        const invertButtons = ClientIdHelper.invertPermissionsButtonsOrder(this.props.clientId);
        let sectionClasses = 'od-ModifyPermissions-section';
        if (this.context.sharingStore && !this.context.sharingStore.isAudienceModificationAllowed()) {
            sectionClasses += ' od-ModifyPermissions-section-noAudienceMod';
        }

        return (
            <div className={ 'od-ModifyPermissions' + blockerClass }>
                <Header
                    clientId={ this.props.clientId }
                    item={ this.props.sharingInformation.item }
                    viewState={ ShareViewState.modifyPermissions }
                    mode={ this.props.mode }
                />
                <div className={ sectionClasses }>
                    <PermissionsSettings
                        mode={ this.props.mode }
                        shareId={ this.props.shareId }
                        companyName={ ShareHelper.truncateOrganizationName(this.props.companyName) }
                        clientId={ this.props.clientId }
                        currentSettings={ this.props.currentSettings }
                        onAudienceChange={ this.onAudienceChange }
                        onEditChange={ this.onEditChange }
                        onRoleChange={ this.onRoleChange }
                        onExpirationChange={ this.onExpirationChange }
                        onPeoplePickerChange={ this._onPeoplePickerChange }
                        peoplePickerError={ this.state.peoplePickerError }
                        selectedPermissions={ this.state.selectedPermissions }
                        sharingInformation={ this.props.sharingInformation }
                        showExistingAccessOption={ this.props.showExistingAccessOption }
                        updateExpirationErrorCode={ this._updateExpirationErrorCode }
                        groupsMemberCount={ this.props.groupsMemberCount }
                        createPeopleSharingLinks={ this.props.createPeopleSharingLinks }
                        copyLinkShortcut={ this.props.copyLinkShortcut }
                        onPasswordChange={ this._onPasswordChange }
                        onToggleChange={ this._onToggleChange }
                        engagementExtraData={ this.props.engagementExtraData }
                        recipients={ this.props.recipients }
                        isLoadingDefaultRecipients={ this.props.isLoadingDefaultRecipients }
                        customLabeledAudienceConfig={ this.props.customLabeledAudienceConfig }
                        preferCustomLabeledAudience={ this.props.preferCustomLabeledAudience }
                    />
                </div>
                { invertButtons ?
                    (
                        <div className='od-ModifyPermissions-actions'>
                            { this._renderDefaultButton() }
                            { this._renderPrimaryButton() }
                        </div>
                    ) :
                    (
                        <div className='od-ModifyPermissions-actions'>
                            { this._renderPrimaryButton() }
                            { this._renderDefaultButton() }
                        </div>
                    )
                }
                { this._renderActivityIndicator() }
                { this.state.showConfirmationDialog && this._renderConfirmationDialog() }
            </div>
        );
    }

    public onAudienceChange(audience: SharingAudience) {
        const selectedPermissions = this.state.selectedPermissions;
        const isEdit = selectedPermissions.isEdit;
        const allowEditing = this._isAllowEditingEnabled(audience);
        const role = allowEditing && isEdit;
        const linkKind = getLinkKind(audience, role);
        const expiration = audience === SharingAudience.anyone ? selectedPermissions.expiration : getDefaultExpirationDateValue(this.props.sharingInformation.anonymousLinkExpirationRestrictionDays);

        let newSettings: ISharingLinkSettings = {
            allowEditing: allowEditing,
            audience: audience,
            expiration: expiration,
            isEdit: role,
            sharingLinkKind: linkKind,
            specificPeople: [],
            updatePassword: false,
            hasPassword: false,
            password: "",
            shareId: ''
        };

        this.setState({
            ...this.state,
            peoplePickerError: null,
            selectedPermissions: newSettings
        });
    }

    public onEditChange(value) {
        const selectedPermissions = this.state.selectedPermissions;
        const sharingLinkKind = getLinkKind(selectedPermissions.audience, value);
        // On a role change, password should get reset
        this._resetPasswordVariables();
        this.setState({
            ...this.state,
            selectedPermissions: {
                ...selectedPermissions,
                isEdit: value,
                sharingLinkKind: sharingLinkKind,
                preventDownload: false
            },
            preventDownloadDisabled: value
        });

        this._preventDownloadToggle = false; // reset back to false if Edit changes.
    }

    public onRoleChange(value) {
        const selectedPermissions = this.state.selectedPermissions;
        const sharingLinkKind = getLinkKind(selectedPermissions.audience, value === SharingRole.edit);
        const expiration = getDefaultExpirationDateValue(this.props.sharingInformation.anonymousLinkExpirationRestrictionDays);

        this.setState({
            ...this.state,
            selectedPermissions: {
                ...selectedPermissions,
                sharingRole: value,
                isEdit: value === SharingRole.edit,
                expiration: expiration,
                sharingLinkKind: sharingLinkKind
            },
            preventDownloadDisabled: value === SharingRole.edit // Disable prevent download if isEdit is true.
        });
    }

    /**
     * Sets selected date as the expiration. If no expiration is passed (i.e. null),
     * we'll determine the default expiration value depending on tenant settings.
     */
    public onExpirationChange(expiration: Date) {
        if (!expiration) {
            expiration = getDefaultExpirationDateValue(this.props.sharingInformation.anonymousLinkExpirationRestrictionDays);
        }

        this.setState({
            ...this.state,
            selectedPermissions: {
                ...this.state.selectedPermissions,
                expiration: expiration
            }
        });
    }

    private _renderDefaultButton() {
        return (
            <DefaultButton onClick={ this._onCancel }>{ stringsResx.cancelButtonText }</DefaultButton>
        );
    }

    private _renderPrimaryButton() {
        return (
            <PrimaryButton
                className='od-ModifyPermissions-apply'
                disabled={ this._computeIsApplyButtonDisabled() }
                onClick={ this._onApplyClicked }
            >{ stringsResx.applyButtonText }</PrimaryButton>
        );
    }

    private _renderActivityIndicator(): React.ReactElement<{}> {
        if (this.state.showActivityIndicator) {
            return (
                <ActivityIndicator message={ stringsResx.activityMessageCreatingLink } />
            );
        }
    }

    private _initializeState() {
        const props = this.props;
        const currentSettings = props.currentSettings;
        const sharingInformation = props.sharingInformation;

        const peoplePickerError = PeoplePickerHelper.renderPickerError({
            selectedItems: currentSettings.specificPeople,
            sharingLinkKind: SharingLinkKind.direct,
            canAddExternalPrincipal: sharingInformation.canAddExternalPrincipal,
            sharingInformation: sharingInformation,
            viewPolicyTipCallback: props.onViewPolicyTipClicked,
        });

        return {
            expirationErrorCode: ExpirationErrorCode.none,
            peoplePickerError: peoplePickerError,
            selectedPermissions: {
                allowEditing: this._isAllowEditingEnabled(currentSettings.audience),
                audience: currentSettings.audience,
                isEdit: currentSettings.isEdit,
                sharingRole: currentSettings.sharingRole,
                expiration: currentSettings.expiration || getDefaultExpirationDateValue(this.props.sharingInformation.anonymousLinkExpirationRestrictionDays),
                sharingLinkKind: currentSettings.sharingLinkKind,
                specificPeople: currentSettings.specificPeople,
                updatePassword: currentSettings.updatePassword,
                password: currentSettings.password,
                hasPassword: currentSettings.hasPassword,
                preventDownload: currentSettings.preventDownload,
                shareId: currentSettings.shareId
            },
            showActivityIndicator: false,
            preventDownloadDisabled: this._isAllowEditingEnabled(currentSettings.audience),
            showConfirmationDialog: false
        };
    }

    /**
     * Disables "Apply" button if there are any errors.
     */
    private _computeIsApplyButtonDisabled(): boolean {
        return this.state.expirationErrorCode !== ExpirationErrorCode.none ||
            !!this.state.peoplePickerError;
    }

    private _updateExpirationErrorCode(code: ExpirationErrorCode) {
        if (code !== this.state.expirationErrorCode) {
            this.setState({
                ...this.state,
                expirationErrorCode: code
            });
        }
    }

    private _onCancel(): void {
        this.props.onCancel();
    }

    private _onApplyClicked(): void {
        const state = this.state;
        const selectedPermissions = state.selectedPermissions;
        /**
         * - Check if there's any unresolved text in the PeoplePicker.
         * - Ensure there is at least 1 recipient if creating a PSL.
         */
        const peoplePickerInput = document.querySelector('.od-Share-PeoplePicker .ms-BasePicker-input') as HTMLInputElement;
        if (peoplePickerInput && peoplePickerInput.value) {
            this.setState({
                ...this.state,
                peoplePickerError: <span>{ stringsResx.unresolvedTextError }</span>
            });
            return;
        } else if (selectedPermissions.audience === SharingAudience.specificPeopleFlex &&
            selectedPermissions.specificPeople.length === 0 &&
            (this.props.copyLinkShortcut || this.props.mode === Mode.linkSettings)) {
            this.setState({
                ...this.state,
                peoplePickerError: <span>{ stringsResx.recipientsRequiredError }</span>
            });
            return;
        }

        // Show activity indicator if view creates links.
        const showActivityIndicatorState = this.props.doesCreate;

        // Clear expiration if link type doesn't support it.
        const expirationState = selectedPermissions.audience !== SharingAudience.anyone ? null : selectedPermissions.expiration;
        const shouldRenderPopUp = this._shouldRenderPopUp(expirationState);
        const passwordProtected = this._matchingTemplate ? this._matchingTemplate.passwordProtected : false;
        if (shouldRenderPopUp) {
            // Preexisting link template exists, with either a new password and/or a changed expiration date
            this.setState({
                ...state,
                selectedPermissions: {
                    ...selectedPermissions,
                    expiration: expirationState,
                    updatePassword: this._shouldUsePassword,
                    password: this._currentPassword,
                    preventDownload: this._preventDownloadToggle,
                    shareId: this._templateShareId,
                    hasPassword: passwordProtected
                },
                showActivityIndicator: showActivityIndicatorState,
                showConfirmationDialog: true
            });
        } else {
            // If we don't have an expiration date set, nor do we have a password set, nor do we have a preexisting link template, then we don't need to display a popup
            this.setState({
                ...state,
                selectedPermissions: {
                    ...selectedPermissions,
                    expiration: expirationState,
                    updatePassword: this._shouldUsePassword,
                    password: this._currentPassword,
                    preventDownload: this._preventDownloadToggle,
                },
                showActivityIndicator: showActivityIndicatorState,
                showConfirmationDialog: false
            }, () => {
                this.props.onSelectedPermissionsChange(this.state.selectedPermissions);
            });
        }
    }
    /**
     * Determines if we should render a confirmation dialog. We only render if expiration or
     * password have been changed from the previous link settings
     *
     */
    private _shouldRenderPopUp(expirationState: Date) {
        // We don't want to check templates for nonanonymous links
        if (this.state.selectedPermissions.audience !== SharingAudience.anyone) {
            return false;
        }
        const hasPassword = this._shouldUsePassword;
        const matchingTemplate = this._getShareLinkTemplate(hasPassword, true);
        // If no preexisting link template exists then we're not overwriting anything
        if (!matchingTemplate || !matchingTemplate.linkDetails) {
            return false;
        }
        // Check 2 cases: Preexisting password exists and we're setting a new one, and we set a new expiration
        if (hasPassword && matchingTemplate.passwordProtected) {
            return true;
        }
        const prevExpiration = matchingTemplate.linkDetails.expiration;
        if (expirationState !== null && prevExpiration !== null &&
            (expirationState.getMonth() !== prevExpiration.getMonth() ||
                expirationState.getDate() !== prevExpiration.getDate())) {
            return true;
        }
        return false;
    }

    private _onPeoplePickerChange(items: Array<IPerson>) {
        let peoplePickerError = PeoplePickerHelper.renderPickerError({
            selectedItems: items,
            sharingLinkKind: SharingLinkKind.direct,
            canAddExternalPrincipal: this.props.sharingInformation.canAddExternalPrincipal,
            sharingInformation: this.props.sharingInformation,
            viewPolicyTipCallback: this.props.onViewPolicyTipClicked
        });

        /**
         * If we're creating a "Specific people" link for any reason
         * except for sending it, we need to ensure that there's not
         * a mix of internal and external users.
         */
        if (!this.props.createPeopleSharingLinks && this.state.selectedPermissions.audience === SharingAudience.specificPeople) {
            let hasExternalRecipient = false;
            let hasInternalRecipient = false;

            for (const recipient of items) {
                if (!hasExternalRecipient) {
                    hasExternalRecipient = recipient.isExternal && !recipient.providerName;
                }

                if (!hasInternalRecipient) {
                    hasInternalRecipient = !recipient.isExternal ||
                        (recipient.isExternal && !!recipient.providerName);
                }
            }

            if (hasExternalRecipient && hasInternalRecipient) {
                peoplePickerError = <span>{ stringsResx.mixedRecipientErrorMessage }</span>;
            }
        }

        if (this.props.onSelectedPeopleChange) {
            this.props.onSelectedPeopleChange(items);
        }

        this.setState({
            ...this.state,
            peoplePickerError: peoplePickerError,
            selectedPermissions: {
                ...this.state.selectedPermissions,
                specificPeople: items
            }
        });
    }

    /**
     * Get the matching sharelink template for the role and return it
     * @param requiresPassword Always return the matching template with a password
     * @param updateShareId Update the shareId used to Id the template
     * @param useNonPasswordLink Return the link without a password
     */
    private _getShareLinkTemplate(requiresPassword: boolean, updateShareId: boolean) {
        const matchingTemplate = this._findMatchingShareLinkTemplate(requiresPassword);
        // Only keep track of the shareIds of the templates with passwords
        if (updateShareId && matchingTemplate) {
            this._matchingTemplate = matchingTemplate;
            if (matchingTemplate.passwordProtected && matchingTemplate.linkDetails) {
                this._templateShareId = matchingTemplate.linkDetails.shareId;
            } else {
                this._templateShareId = '';
            }
        }
        return matchingTemplate;
    }

    private _findMatchingShareLinkTemplate(requiresPassword: boolean) {
        let matchingTemplateWithPassword: ISharingLinkTemplate;
        let matchingTemplateWithoutPassword: ISharingLinkTemplate;
        const templateList = this.props.sharingInformation.sharingLinkTemplates;
        // We have no templates, so return null
        if (!templateList || templateList.length === 0) {
            return null;
        }
        const audience = SharingAudienceTemplates.anyone; // Currently only enabled for anonymous links
        // All links with passwords are flexible links
        const sharingRole = this._calculateRole();
        let passwordProtected;
        if (requiresPassword) {
            passwordProtected = true;
        } else {
            passwordProtected = this._currentPassword.length > 0;
        }
        for (let template of templateList) {
            // Matching role and audience
            // Commenting out && sharingLinkKind === template.shareKind
            if (template.role === sharingRole && template.scope === audience) {
                if (passwordProtected && template.passwordProtected) {
                    // We only want the passworded template
                    return template;
                } else {
                    if (template.passwordProtected) {
                        matchingTemplateWithPassword = template;
                    } else {
                        matchingTemplateWithoutPassword = template;
                    }
                }
            }
        }
        // Only return nonpassword link if password link doesn't exist, or if we just deleted a password, or if we don't need a password
        if (matchingTemplateWithPassword === null || matchingTemplateWithPassword.linkDetails === null || this._deletedPassword || !requiresPassword) {
            return matchingTemplateWithoutPassword;
        } else {
            return matchingTemplateWithPassword;
        }
    }

    private _calculateRole() {
        if (this.state.selectedPermissions.preventDownload) {
            return SharingRole.restrictedview;
        }
        if (!this.state.selectedPermissions.isEdit) {
            return SharingRole.view;
        }
        return SharingRole.edit;
    }

    /**
     * Updates password. Whenever password length is above 0, we should use it, otherwise shouldUsePassword is false (this variable sets updatePassword)
     * deletedPassword is true when password length becomes 0, because this only happens when the password is deleted, or the user types in something and erases it
     */
    private _onPasswordChange(newPassword: string) {
        this._currentPassword = newPassword;
        this._shouldUsePassword = newPassword.length > 0;
        this._deletedPassword = newPassword.length === 0;
        this.setState({
            ...this.state
        });
    }

    /**
     * Resets password related variables to their default states
     */
    private _resetPasswordVariables() {
        this._currentPassword = '';
        this._shouldUsePassword = false;
        // Deleted password is used as a flag to use the non-password template when a user manually deletes the password. For a reset it should be false
        this._deletedPassword = false;
        this._templateShareId = '';
    }

    private _onToggleChange(value) {
        this._preventDownloadToggle = value;
        // On a role change, password should get reset
        this._resetPasswordVariables();
        this.setState({
            ...this.state,
            selectedPermissions: {
                ...this.state.selectedPermissions,
                preventDownload: value
            }
        });
    }

    private _isAllowEditingEnabled(audience: SharingAudience) {
        const sharingAbilities = this.props.sharingInformation.sharingAbilities;
        return SharingAbilitiesHelper.isEditingAllowed(audience, sharingAbilities);
    }

    /**
     * Check what's been changed between date and password, and return a confirmation code accordingly
     *
     * @param hasPassword
     *
     * @param matchingTemplate
     */
    private _getShareDialogConfirmationCode(hasPassword: boolean, matchingTemplate: ISharingLinkTemplate) {
        const expirationState = this.state.selectedPermissions.expiration;
        const prevExpiration = matchingTemplate.linkDetails.expiration;
        // Check whether we changed the password and/or we changed the expiration date
        const passwordChanged = hasPassword && matchingTemplate.passwordProtected;
        const expirationChanged = expirationState !== null && prevExpiration !== null &&
            (expirationState.getMonth() !== prevExpiration.getMonth() || expirationState.getDate() !== prevExpiration.getDate());
        // Based on whether the password and/or the expiration has changed, return the correct expiration
        if (passwordChanged && expirationChanged) {
            return ConfirmationCode.shareDialogPasswordAndExpiration;
        } else if (passwordChanged) {
            return ConfirmationCode.shareDialogPasswordOnly;
        } else {
            return ConfirmationCode.shareDialogExpirationOnly;
        }
    }

    /**
     * Render confirmation dialogue based on what settings have been changed from the original link.
     *
     */
    private _renderConfirmationDialog() {
        const hasPassword = this._shouldUsePassword;
        const matchingTemplate = this._matchingTemplate;
        let confirmationCode = this._getShareDialogConfirmationCode(hasPassword, matchingTemplate);
        // Name of the person that last changed the password
        const linkPwdSetBy = matchingTemplate && matchingTemplate.linkDetails && matchingTemplate.linkDetails.passwordLastModifiedBy !== null ? matchingTemplate.linkDetails.passwordLastModifiedBy.primaryText : '';
        const linkExpiration = matchingTemplate && matchingTemplate.linkDetails && matchingTemplate.linkDetails.expiration !== null ? getShortDisplayDate(matchingTemplate.linkDetails.expiration) : '';
        return (
            <ConfirmationDialog
                confirmationCode={ confirmationCode }
                participant={ null }
                onApply={ this._onConfirmationUseNew }
                onCancel={ this._onConfirmationUseExisting }
                linkPasswordSetBy={ linkPwdSetBy }
                linkExpiration={ linkExpiration }
            />
        );
    }

    private _onConfirmationUseNew(): void {
        this.setState(
            {
                ...this.state,
                showConfirmationDialog: false,
            }, () => {
                this.props.onSelectedPermissionsChange(this.state.selectedPermissions);
            });
    }

    private _onConfirmationUseExisting(): void {
        const state = this.state;
        const selectedPermissions = state.selectedPermissions;
        const expiration = this._matchingTemplate && this._matchingTemplate.linkDetails ? this._matchingTemplate.linkDetails.expiration : null;
        const hasPassword = this._matchingTemplate && this._matchingTemplate.linkDetails ? this._matchingTemplate.passwordProtected : false;
        this.setState(
            {
                ...this.state,
                selectedPermissions: {
                    ...selectedPermissions,
                    expiration: expiration,
                    updatePassword: false,
                    password: '',
                    hasPassword: hasPassword
                },
                showConfirmationDialog: false,
            }, () => {
                this.props.onSelectedPermissionsChange(this.state.selectedPermissions);
            });
    }
}
